import React, { useState } from 'react';
import {
  CompetitorItem,
  CallInsightsAnalysis,
  getScoreBadgeClasses,
  getAdherenceLabelClasses,
  formatAdherenceLabel,
  formatCallInsightsTimestamp,
} from './types';
import SentimentRow from './SentimentRow';
import TranscriptBlock from './TranscriptBlock';
import ManagerReviewPanel from './ManagerReviewPanel';

interface CompetitorAdherenceSectionProps {
  items: CompetitorItem[];
  overall_summary: string;
  sessionId: string;
  managerReview?: CallInsightsAnalysis['manager_review'];
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

const CompetitorAdherenceSection: React.FC<CompetitorAdherenceSectionProps> = ({
  items,
  overall_summary,
  sessionId,
  managerReview,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
}) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [expandedIntel, setExpandedIntel] = useState<Set<number>>(new Set());
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<number>>(new Set());

  const toggleCard = (idx: number) => {
    setExpandedCard((prev) => (prev === idx ? null : idx));
  };

  const toggleIntel = (idx: number) => {
    setExpandedIntel((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleTranscripts = (idx: number) => {
    setExpandedTranscripts((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 text-ink-muted">
        No competitor mentions in this session.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {overall_summary && (
        <div className="bg-info-tint border border-info rounded-lg p-4 text-sm text-ink">
          <span className="font-semibold">Overall Summary: </span>
          {overall_summary}
        </div>
      )}

      {items.map((item, idx) => {
        const isOpen = expandedCard === idx;
        const hasIntel =
          (item.kb_advantages && item.kb_advantages.length > 0) ||
          (item.kb_weaknesses && item.kb_weaknesses.length > 0) ||
          !!item.kb_talking_points;

        return (
          <div
            key={idx}
            className="bg-white border border-hairline rounded-lg shadow-sm overflow-hidden"
          >
            {/* Clickable header — always visible */}
            <button
              type="button"
              onClick={() => toggleCard(idx)}
              className="w-full flex flex-wrap items-start justify-between gap-2 p-4 text-left hover:bg-surface-subtle transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink text-sm leading-snug">
                  {item.competitor}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {item.triggered_by && (
                    <span className="text-xs text-ink-muted">
                      Mentioned by: <span className="font-medium capitalize">{item.triggered_by}</span>
                    </span>
                  )}
                  {item.timestamp && (
                    <span className="text-xs text-ink-muted">{formatCallInsightsTimestamp(item.timestamp)}</span>
                  )}
                  {item.timestamp_iso && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onNavigateToTimestamp?.(item.timestamp_iso!); }}
                      className="text-xs text-signature hover:text-signature-strong font-medium underline"
                    >
                      View in Timeline &rarr;
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}
                >
                  {item.handling_score}/100
                </span>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.handling_label)}`}
                >
                  {formatAdherenceLabel(item.handling_label)}
                </span>
                <span className="text-ink-muted text-xs ml-1">{isOpen ? '▲' : '▼'}</span>
              </div>
            </button>

            {/* Collapsible body */}
            {isOpen && (
              <div className="p-4 border-t border-hairline space-y-3">
                {item.handling_summary && (
                  <p className="text-sm text-ink-secondary">{item.handling_summary}</p>
                )}

                {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {item.what_worked && (
                      <div className="flex items-start gap-2 bg-success-tint border border-success rounded p-2.5 text-sm">
                        <span className="text-success font-bold flex-shrink-0 mt-0.5">&#10003;</span>
                        <p className="text-ink">{item.what_worked}</p>
                      </div>
                    )}
                    {(item.what_missed || item.what_rep_should_have_said) && (
                      <div className="bg-error-tint border border-error rounded p-2.5 text-sm space-y-2">
                        {item.what_missed && (
                          <div className="flex items-start gap-2">
                            <span className="text-error font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                            <p className="text-ink">{item.what_missed}</p>
                          </div>
                        )}
                        {item.what_rep_should_have_said && (
                          <div className={item.what_missed ? 'pt-2 border-t border-error' : ''}>
                            <p className="text-xs font-semibold text-error uppercase mb-1">Should Have Said</p>
                            <p className="text-ink">{item.what_rep_should_have_said}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {item.customer_reaction && (
                  <p className="text-sm text-ink-muted italic">
                    Customer reaction: {item.customer_reaction}
                  </p>
                )}

                <SentimentRow
                  before={item.sentiment_at_detection}
                  after={item.sentiment_after}
                  labelBefore="At Mention"
                  labelAfter="After"
                />

                {hasIntel && (
                  <div>
                    <button
                      onClick={() => toggleIntel(idx)}
                      className="text-sm text-signature hover:text-signature-strong font-medium focus:outline-none"
                      type="button"
                    >
                      {expandedIntel.has(idx) ? 'Hide Competitive Intelligence' : 'Show Competitive Intelligence'}
                      <span className="ml-1">{expandedIntel.has(idx) ? '▲' : '▼'}</span>
                    </button>
                    {expandedIntel.has(idx) && (
                      <div className="mt-2 space-y-3">
                        {item.kb_advantages && item.kb_advantages.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-success uppercase mb-1">Our Advantages</p>
                            <ul className="space-y-1">
                              {item.kb_advantages.map((adv, i) => (
                                <li key={i} className="text-sm text-ink bg-success-tint border border-success rounded px-2 py-1">
                                  {adv}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.kb_weaknesses && item.kb_weaknesses.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-ink-secondary uppercase mb-1">Their Weaknesses</p>
                            <ul className="space-y-1">
                              {item.kb_weaknesses.map((wk, i) => (
                                <li key={i} className="text-sm text-ink bg-surface-subtle border border-hairline rounded px-2 py-1">
                                  {wk}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.kb_talking_points && (
                          <div className="bg-surface-subtle border border-hairline rounded p-3 text-sm text-ink-secondary">
                            <p className="font-semibold text-ink mb-1">Talking Points</p>
                            <p>{item.kb_talking_points}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <TranscriptBlock
                  transcripts={item.relevant_transcripts ?? []}
                  isExpanded={expandedTranscripts.has(idx)}
                  onToggle={() => toggleTranscripts(idx)}
                />
              </div>
            )}
          </div>
        );
      })}

      <ManagerReviewPanel
        sessionId={sessionId}
        currentSection="competitor"
        managerReview={managerReview}
        isAdmin={isAdmin}
        onReviewSaved={onReviewSaved}
      />
    </div>
  );
};

export default CompetitorAdherenceSection;
